class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
   // const cartLink = document.querySelector('#cart-icon-bubble');
    const cartLink = document.querySelector('.kov-js-cart-open');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      if (typeof window.initCLTUpsell === 'function') window.initCLTUpsell();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.updateKovosisHeaderCounts();
    if (typeof window.initCLTUpsell === 'function') window.initCLTUpsell(true);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  updateKovosisHeaderCounts() {
    fetch(`${routes.cart_url}.json`)
      .then((res) => res.json())
      .then((cart) => this.applyKovosisHeaderCounts(cart))
      .catch(() => {});
  }

  applyKovosisHeaderCounts(cart) {
    const visibleCount = CartDrawer.getVisibleCount(cart);
    document.querySelectorAll('.kov-js-cart-count').forEach((el) => {
      el.textContent = visibleCount;
    });
    document.querySelectorAll('.kov-js-cart-total').forEach((el) => {
      el.textContent = CartDrawer.formatKovosisMoney(cart.total_price);
    });
  }

  static getVisibleCount(cart) {
    if (!cart) return 0;
    const spVariantId = window.KovosisSpVariantId;
    if (!spVariantId || !cart.items) return cart.item_count || 0;
    const spItem = cart.items.find(
      (item) => item.variant_id === spVariantId || item.id === spVariantId
    );
    const spQty = spItem ? spItem.quantity : 0;
    return (cart.item_count || 0) - spQty;
  }

  static formatKovosisMoney(cents) {
    const format = window.KovosisMoneyFormat || '${{amount}}';
    const val = (cents / 100).toFixed(2);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return val;
    const token = match[1];
    let display;
    switch (token) {
      case 'amount':
        display = val;
        break;
      case 'amount_no_decimals':
        display = Math.round(cents / 100).toString();
        break;
      case 'amount_with_comma_separator':
        display = val.replace('.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        display = Math.round(cents / 100).toString();
        break;
      default:
        display = val;
    }
    return format.replace(placeholder, display);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    // Subscribe to ALL cartUpdate events (including cart-items source,
    // which the parent class skips) to keep kovosis header counts in sync
    // on quantity changes and removals inside the drawer.
    this.kovosisCartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.updateKovosisHeaderCounts();
      if (typeof window.initCLTUpsell === 'function') window.initCLTUpsell(true);
    });
  }

  disconnectedCallback() {
    if (this.kovosisCartUpdateUnsubscriber) {
      this.kovosisCartUpdateUnsubscriber();
    }
    super.disconnectedCallback();
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
